<template>
  <div>
    <div class="row">
      <div v-if="isVisible('first_name')" class="col-md-6">
        <div :class="{'has-error': errors.has('first_name')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.firstName') }}<small v-if="!isRequired('first_name')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="first_name-input"
            v-model="value.first_name"
            v-validate="{required: isRequired('first_name')}"
            type="text"
            name="first_name"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('first_name')" class="error-list">
            <li>{{ errors.first('first_name') }}</li>
          </ul>
        </div>
      </div>
      <div v-if="isVisible('last_name')" class="col-md-6">
        <div :class="{'has-error': errors.has('last_name')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.lastName') }}<small v-if="!isRequired('last_name')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="last_name-input"
            v-model="value.last_name"
            v-validate="{required: isRequired('last_name')}"
            type="text"
            name="last_name"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('last_name')" class="error-list">
            <li>{{ errors.first('last_name') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('email_address')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('email')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.emailAddress') }}<small v-if="!isRequired('email_address')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="email-input"
            v-model="value.email"
            v-validate="{required: isRequired('email_address'), email: true}"
            type="email"
            name="email"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('email')" class="error-list">
            <li>{{ errors.first('email') }}</li>
          </ul>
          <div v-if="showLoginLinkAlertForField('email')" class="alert alert-danger">
            <i18n path="enrollmentStep.accountCreation.alreadyCreatedAccount">
              <router-link :to="{name: 'login'}">
                {{ $t('enrollmentStep.accountCreation.clickHereToLogin') }}
              </router-link>
            </i18n>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isVisible('username')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('username')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.username') }}<small v-if="!isRequired('username')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="username-input"
            v-model="value.username"
            v-validate="{required: isRequired('username'), valid_username: true, short_username: true, long_username: true}"
            type="text"
            name="username"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('username')" class="error-list">
            <li>{{ errors.first('username') }}</li>
          </ul>
          <div v-if="showLoginLinkAlertForField('username')" class="alert alert-danger">
            <i18n path="enrollmentStep.accountCreation.alreadyCreatedAccount">
              <router-link :to="{name: 'login'}">
                {{ $t('enrollmentStep.accountCreation.clickHereToLogin') }}
              </router-link>
            </i18n>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isVisible('locale')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('locale')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.locale') }}<small v-if="!isRequired('locale')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <select class="form-control" @change="updateLocale">
            <option v-for="locale in enabledLocales" :value="locale.locale">
              {{ locale.name }}
            </option>
          </select>
          <ul v-if="errors.has('locale')" class="error-list">
            <li>{{ errors.first('locale') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('password')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('password')}" class="form-group">
          <label>{{ $t('enrollmentStep.profileFields.password') }}:</label>
          <password
            v-model="value.password"
            v-validate="'required|strong_password'"
            :badge="false"
            :required="false"
            name="password"
            placeholder=""
            defaultClass="form-control"
            :toggle="true"
            @score="(score) => passwordStrength = score"
            @focus="$emit('focus', $event)"
          />
          <ul v-if="errors.has('password') || showPasswordHints" class="error-list" id="password-errors">
            <li v-if="passwordHints.warning">
              {{ passwordHints.warning }}
            </li>
            <li v-for="suggestion in passwordHints.suggestions">
              {{ suggestion }}
            </li>
            <li>{{ errors.first('password') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('cell_phone') && !showNotifyText" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('cell_phone')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.cellPhone') }}<small v-if="!isRequired('cell_phone')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="cell_phone-input"
            v-model="value.cell_phone"
            v-mask="'(###) ###-####'"
            v-validate="{required: isRequired('cell_phone'), regex: /^\(\d{3}\)\s\d{3}-\d{4}$/}"
            type="tel"
            name="cell_phone"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('cell_phone')" class="error-list">
            <li>{{ errors.first('cell_phone') }}</li>
          </ul>
          <div v-if="showLoginLinkAlertForField('cell_phone')" class="alert alert-danger">
            <i18n path="enrollmentStep.accountCreation.alreadyCreatedAccount">
              <router-link :to="{name: 'login'}">
                {{ $t('enrollmentStep.accountCreation.clickHereToLogin') }}
              </router-link>
            </i18n>
          </div>
        </div>
      </div>
    </div>
    <div v-if="isVisible('home_phone') && !showNotifyVoice" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('home_phone')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.homePhone') }}<small v-if="!isRequired('home_phone')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="home_phone-input"
            v-model="value.home_phone"
            v-mask="'(###) ###-####'"
            v-validate="{required: isRequired('home_phone'), regex: /^\(\d{3}\)\s\d{3}-\d{4}$/}"
            type="tel"
            name="home_phone"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('home_phone')" class="error-list">
            <li>{{ errors.first('home_phone') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('street_address')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('street_address')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.streetAddress') }}<small v-if="!isRequired('street_address')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="street_address-input"
            v-model="value.street_address"
            v-validate="{required: isRequired('street_address')}"
            type="text"
            name="street_address"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('street_address')" class="error-list">
            <li>{{ errors.first('street_address') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('street_address2')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('street_address2')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.streetAddress2') }}<small> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="street_address2-input"
            v-model="value.street_address2"
            type="text"
            name="street_address2"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
        </div>
      </div>
    </div>

    <div class="row">
      <div v-if="isVisible('city')" class="col-md-5">
        <div :class="{'has-error': errors.has('city')}" class="form-group city-group">
          <label>
            {{ $t('enrollmentStep.profileFields.city') }}<small v-if="!isRequired('city')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="city-input"
            v-model="value.city"
            v-validate="{required: isRequired('city')}"
            type="text"
            name="city"
            class="form-control city"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('city')" class="error-list">
            <li>{{ errors.first('city') }}</li>
          </ul>
        </div>
      </div>
      <div v-if="isVisible('state_region')" class="col-md-4">
        <div :class="{'has-error': errors.has('state_region')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.state') }}<small v-if="!isRequired('state_region')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <StateSelect
            key="state_region-input"
            v-model="value.state_region"
            v-validate="{required: isRequired('state_region')}"
            class="form-control"
            name="state_region"
            @focus="$emit('focus', $event)"
          />
          <ul v-if="errors.has('state_region')" class="error-list">
            <li>{{ errors.first('state_region') }}</li>
          </ul>
        </div>
      </div>
      <div v-if="isVisible('postal_code')" class="col-md-3">
        <div :class="{'has-error': errors.has('postal_code')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.postal_code') }}<small v-if="!isRequired('postal_code')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="postal_code-input"
            v-model="value.postal_code"
            v-validate="{required: isRequired('postal_code')}"
            type="text"
            name="postal_code"
            class="form-control"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('postal_code')" class="error-list">
            <li>{{ errors.first('postal_code') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('time_zone')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('time_zone')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.timeZone') }}<small v-if="!isRequired('time_zone')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <TimezoneSelect
            key="time_zone-input"
            v-model="value.time_zone"
            v-validate="{required: isRequired('time_zone')}"
            name="time_zone"
            class="form-control"
            @focus="$emit('focus', $event)"
          />
          <ul v-if="errors.has('time_zone')" class="error-list">
            <li>{{ errors.first('time_zone') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('date_of_birth')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('date_of_birth')}" class="form-group">
          <label>
            {{ $t('enrollmentStep.profileFields.dob') }}<small v-if="!isRequired('date_of_birth')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <DateInput v-model="value.date_of_birth" v-validate="{required: isRequired('date_of_birth'), before: todayInISO, date_format: 'yyyy-MM-dd'}" name="date_of_birth" class="form-control" />
          <ul v-if="errors.has('date_of_birth')" class="error-list">
            <li>{{ errors.first('date_of_birth') }}</li>
          </ul>
        </div>
      </div>
    </div>

    <div v-if="isVisible('external_id')" class="row">
      <div class="col-md-12">
        <div :class="{'has-error': errors.has('external_id')}" class="form-group">
          <label>
            <!-- @TODO: config.external_id_label needs to come in translated -->
            {{ config.external_id_label || $t('enrollmentStep.profileFields.externalId') }}<small v-if="!isRequired('external_id')"> ({{ $t('enrollmentStep.profileFields.optional') }})</small>:
          </label>
          <input
            key="external_id-input"
            v-model="value.external_id"
            v-validate="{required: isRequired('external_id')}"
            type="text"
            name="external_id"
            class="form-control"
            :data-vv-as="config.external_id_label || $t('enrollmentStep.profileFields.externalId')"
            @focus="$emit('focus', $event)"
          >
          <ul v-if="errors.has('external_id')" class="error-list">
            <li>{{ errors.first('external_id') }}</li>
          </ul>
          <div v-if="showLoginLinkAlertForField('external_id')" class="alert alert-danger">
            <i18n path="enrollmentStep.accountCreation.alreadyCreatedAccount">
              <router-link :to="{name: 'login'}">
                {{ $t('enrollmentStep.accountCreation.clickHereToLogin') }}
              </router-link>
            </i18n>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showNotifyPrefs" class="row">
      <div class="col-md-12">
        <fieldset>
          <legend>{{ $t('enrollmentStep.profileFields.notificationPreferences') }}</legend>
          <p>{{ $t('enrollmentStep.profileFields.howToGetNotifications') }}</p>
          <div v-if="errors.has('notify_email') || errors.has('notify_text') || errors.has('notify_voice')" class="alert alert-danger">
            {{ $t('enrollmentStep.profileFields.mustSelectOne') }}
          </div>
          <div v-if="showNotifyEmail" class="checkbox">
            <label>
              <input
                v-model="value.notify_email"
                v-validate="!notifySelected ? 'required' :''"
                name="notify_email"
                type="checkbox"
                @focus="$emit('focus', $event)"
              > {{ $t('enrollmentStep.profileFields.email') }}
            </label>
          </div>
          <div v-if="showNotifyText" class="checkbox">
            <label>
              <input
                v-model="value.notify_text"
                v-validate="!notifySelected ? 'required' :''"
                name="notify_text"
                type="checkbox"
                @focus="$emit('focus', $event)"
                @change="clearTextNumberField"
              > {{ $t('enrollmentStep.profileFields.textMessage') }}
            </label>
            <div v-show="value.notify_text">
              <div class="col-md-12">
                <div :class="{'has-error': errors.has('cell_phone')}" class="form-group">
                  <input
                    key="cell_phone-input"
                    v-model="value.cell_phone"
                    v-mask="'(###) ###-####'"
                    v-validate="{required: isRequired('cell_phone'), regex: /^\(\d{3}\)\s\d{3}-\d{4}$/}"
                    type="tel"
                    name="cell_phone"
                    :placeholder="$t('enrollmentStep.profileFields.cellPhone')"
                    class="form-control form-control-sm"
                    @focus="$emit('focus', $event)"
                  >
                  <ul v-if="errors.has('cell_phone')" class="error-list">
                    <li>{{ errors.first('cell_phone') }}</li>
                  </ul>
                  <div v-if="showLoginLinkAlertForField('cell_phone')" class="alert alert-danger">
                    <i18n path="enrollmentStep.accountCreation.alreadyCreatedAccount">
                      <router-link :to="{name: 'login'}">
                        {{ $t('enrollmentStep.accountCreation.clickHereToLogin') }}
                      </router-link>
                    </i18n>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-if="showNotifyVoice" class="checkbox">
            <label>
              <input
                v-model="value.notify_voice"
                v-validate="!notifySelected ? 'required' :''"
                name="notify_voice"
                type="checkbox"
                @focus="$emit('focus', $event)"
                @change="clearVoiceNumberField"
              > {{ $t('enrollmentStep.profileFields.phoneCall') }}
            </label>
            <div v-show="value.notify_voice">
              <div class="col-md-12">
                <div :class="{'has-error': errors.has('home_phone')}" class="form-group">
                  <input
                    key="home_phone-input"
                    v-model="value.home_phone"
                    v-mask="'(###) ###-####'"
                    v-validate="{required: isRequired('home_phone'), regex: /^\(\d{3}\)\s\d{3}-\d{4}$/}"
                    type="tel"
                    name="home_phone"
                    :placeholder="$t('enrollmentStep.profileFields.homePhone')"
                    class="form-control form-control-sm"
                    @focus="$emit('focus', $event)"
                  >
                  <ul v-if="errors.has('home_phone')" class="error-list">
                    <li>{{ errors.first('home_phone') }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  </div>
</template>

<script>
  import { mask } from 'vue-the-mask';
  import StateSelect from '@ui/forms/fields/StateSelect.vue';
  import TimezoneSelect from '@ui/forms/fields/TimezoneSelect.vue';
  import DateInput from '@ui/forms/fields/DateInput.vue';
  import Password from 'vue-password-strength-meter';
  import zxcvbn from 'zxcvbn';
  import { formatISO } from "date-fns";


  export default {
    components: {
      StateSelect,
      TimezoneSelect,
      Password,
      DateInput
    },
    directives: {
      mask
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      config: {
        type: Object,
        default: () => {
          return {};
        }
      },
      isLoginEnabled: {
        type: Boolean,
        default: true
      },
      accountId: {
        type: Number,
        required: false,
      }
    },
    computed: {
      showNotifyPrefs() {
        return this.config && this.config.notify_prefs && (this.showNotifyEmail || this.showNotifyText || this.showNotifyVoice);
      },
      showNotifyEmail() {
        return this.isVisible('email_address') && this.config.notify_email;
      },
      showNotifyText() {
        return this.isVisible('cell_phone') && this.config.notify_text;
      },
      showNotifyVoice() {
        return this.isVisible('home_phone') && this.config.notify_voice;
      },
      // used for validation
      notifySelected() {
        return this.value.notify_email || this.value.notify_text || this.value.notify_voice;
      },
      passwordHints() {
        return zxcvbn(this.value.password).feedback;
      },
      showPasswordHints() {
        if (!this.isVisible('password')) {
          return false;
        }
        return !!this.value.password
          && this.passwordHints.suggestions.length > 0;
      },
      firstUniqueErrorField() {
        // show for the first field with a unique error
        const fields = ['email', 'username', 'cell_phone', 'external_id'];
        for (const field of fields) {
          if (this.errors.firstByRule(field, `unique_${field}`)) {
            return field;
          }
        }
        return false;
      },
      showLoginLinkAlertEmail() {
        return this.errors.firstByRule('email', 'unique_email')
            && this.isLoginEnabled
            && !this.accountId;
      },
      showLoginLinkAlertUsername() {
        return this.errors.firstByRule('username', 'unique_username')
            && this.isLoginEnabled
            && !this.accountId;
      },
      todayInISO() {
        return formatISO(new Date(), {representation: 'date'});
      },
      enabledLocales() {
        const allLocales = [
          {locale: 'en', name: 'English'},
          {locale: 'es', name: 'Español'},
          {locale: 'ko', name: '한국어'},
          {locale: 'ru', name: 'русский'},
          {locale: 'zh', name: '简体中文'},
          {locale: 'vi', name: 'Tiếng Việt'},
          {locale: 'pt', name: 'Português'},
          {locale: 'ht', name: 'Kreyòl Ayisyen'}
        ];
        return allLocales.filter(l => this.$store.state.locales.includes(l.locale));
      },
    },
    created() {
      this.$validator.extend('valid_username', {
        getMessage: (field) => this.$t('enrollmentStep.profileFields.username') + ` ` + this.$t('usernameHint'),
        validate: value => {
          return {
            valid: !!value.match(/^[\w.-]+$/),
            data: value
          };
        }
      });

      this.$validator.extend('short_username', {
        getMessage: (field) => this.$t('enrollmentStep.profileFields.username') + ` ` + this.$t('tooShort'),
        validate: value => {
          return {
            valid: value.length >= 3,
            data: value
          };
        }
      });

      this.$validator.extend('long_username', {
        getMessage: (field) => this.$t('enrollmentStep.profileFields.username') + ` ` + this.$t('tooLong'),
        validate: value => {
          return {
            valid: value.length <= 36,
            data: value
          };
        }
      });

      this.$validator.extend('strong_password', {
        getMessage: (field) => this.$t('enrollmentStep.profileFields.notStrong'),
        validate: value => {
          return {
            valid: zxcvbn(value).score >= 2,
            data: value
          };
        }
      });
    },
    methods: {
      isVisible(field) {
        return this.config && this.config[field] !== undefined && this.config[field] !== 'hidden';
      },
      isRequired(field) {
        // conditionally require email_address if chosen to notify via email
        if (field === 'email_address' && this.value.notify_email) {
          return true;
        }

        // conditionally require cell_phone if chosen to notify via text
        if (field === 'cell_phone' && this.value.notify_text) {
          return true;
        }
        // conditionally require home_phone if chosen to notify via voice
        if (field === 'home_phone' && this.value.notify_voice) {
          return true;
        }

        return this.config[field] === 'required';
      },
      validate() {
        return this.$validator.validateAll();
      },
      clearVoiceNumberField(event) {
        if (!event.target.checked) {
          this.value.home_phone = "";
        }
      },
      clearTextNumberField(event) {
        if (!event.target.checked) {
          this.value.cell_phone = "";
        }
      },
      showLoginLinkAlertForField(field) {
        return this.firstUniqueErrorField == field
          && this.isLoginEnabled
          && !this.accountId;
      },
      pushLocalizedErrors(field_errors) {
        // loop over each field error and see if any of them are unique constraint errors (since those are the only ones
        // we can't do client side). If so, push translated versions of them to Vee-Validate.
        field_errors.forEach((field_error) => {
          console.log(field_error)
          const rule = `unique_${field_error.field}`;
          const error_key  = `veeValidate.errors.${rule}`;
          if (field_error.code.includes('duplicate') && this.$te(error_key)) {
            this.$validator.errors.add({
              field: field_error.field,
              msg: this.$t(error_key),
              rule
            });
          }
        })
      },
      updateLocale(e){
        this.$i18n.locale = e.target.value
      }
    }
  }
</script>


<style scoped>
.Password {
  max-width: none;
  margin: 0 0;
}

/*
These (>>>) are deep selectors which apply to subcomponents.
https://vue-loader.vuejs.org/guide/scoped-css.html#deep-selectors
*/
.Password >>> .Password__strength-meter:before {
  left: 20%;
  width: 20%;
}

.Password >>> .Password__strength-meter:after {
  right: 20%;
  width: 20%;
}

>>> .datepicker {
  display: block;
  z-index: 1040;
}
</style>
